import type { components } from '@/types/api/v1'
import {
  type DiffOptions,
  type FieldDiff,
  diffObjects,
} from '@/utils/objectDiff'

export type DocumentAudit = components['schemas']['DocumentAudit']

/** Identifiers and bookkeeping that are noise in every history view. */
const DEFAULT_IGNORED_KEYS = ['id', '_id', 'revision_id']

export interface HistoryEntry {
  audit: DocumentAudit
  /** What this revision changed, relative to whatever replaced it. */
  changes: FieldDiff[]
  /**
   * What `changes` was computed against: the live document for the newest
   * revision, the next revision for the rest, or null when the newest
   * revision had nothing to compare against.
   */
  comparedTo: 'current' | 'revision' | null
  /**
   * The audit to restore in order to land on this entry's *resulting* state —
   * the right-hand column of its change rows, which is what someone reading
   * the entry means by "put it back to this".
   *
   * That state is the previous entry's stored document, since each record
   * holds the document as it was before its own change. Null on the newest
   * entry, whose result is already the live document.
   */
  restoreAuditId: string | null
}

export interface BuildHistoryOptions extends Pick<DiffOptions, 'ignore'> {
  /**
   * Set when `current` is the full stored document rather than an API
   * projection of it. Projections omit fields, which would otherwise read as
   * the newest revision having removed them.
   */
  currentIsFullDocument?: boolean
}

/**
 * Pairs each audit record with the change it represents.
 *
 * Each record holds the document as it was *before* an update, so a revision
 * is described by diffing it against the next-newer record — and, for the
 * newest record, against the document as it stands now.
 */
export function buildHistoryEntries(
  history: DocumentAudit[],
  current: Record<string, unknown> | undefined,
  options: BuildHistoryOptions = {},
): HistoryEntry[] {
  const newestFirst = [...history].sort(
    (a, b) =>
      new Date(b.updated_on).getTime() - new Date(a.updated_on).getTime(),
  )

  return newestFirst.map((audit, index) => {
    const isNewest = index === 0
    const after = isNewest ? current : newestFirst[index - 1]?.old_document
    const comparedTo = after ? (isNewest ? 'current' : 'revision') : null

    return {
      audit,
      comparedTo,
      restoreAuditId: isNewest ? null : (newestFirst[index - 1]?._id ?? null),
      changes: after
        ? diffObjects(audit.old_document, after, {
            ignore: options.ignore ?? DEFAULT_IGNORED_KEYS,
            sharedKeysOnly: isNewest && !options.currentIsFullDocument,
          })
        : [],
    }
  })
}
